import React, { useState, useRef, useEffect } from 'react'
import type { Collection } from './types'

type CollectionItemProps = {
  collection: Collection
  contentCount: number
  isSelected: boolean
  onSelect: () => void
  onRename: (newTitle: string) => Promise<boolean>
  onDelete: () => void
  canEdit: boolean
}

/**
 * Item de collection avec actions inline (renommer, supprimer)
 * Responsabilité unique : affichage et édition d'une collection
 */
export default function CollectionItem({
  collection,
  contentCount,
  isSelected,
  onSelect,
  onRename,
  onDelete,
  canEdit,
}: CollectionItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(collection.title)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input quand on passe en mode édition
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canEdit) return
    setEditValue(collection.title)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditValue(collection.title)
  }

  const handleSaveEdit = async () => {
    if (!editValue.trim() || editValue.trim() === collection.title) {
      handleCancelEdit()
      return
    }
    setSaving(true)
    const success = await onRename(editValue.trim())
    setSaving(false)
    if (success) {
      setIsEditing(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  const sourceColor = collection.source === 'wordpress' 
    ? 'bg-sky-500' 
    : 'bg-emerald-500'

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 dark:bg-emerald-900/30">
        <span className={`h-2 w-2 shrink-0 rounded-full ${sourceColor}`} />
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSaveEdit}
          disabled={saving}
          className="flex-1 min-w-0 rounded-lg border border-emerald-200 bg-white px-2 py-1 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-200 dark:border-emerald-800 dark:bg-zinc-900 dark:text-zinc-200"
        />
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={handleSaveEdit}
            disabled={saving}
            className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/50"
            title="Enregistrer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleCancelEdit}
            disabled={saving}
            className="rounded-lg p-1 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
            title="Annuler"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => e.key === 'Enter' && onSelect()}
      className={`group flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition cursor-pointer ${
        isSelected
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
          : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800/60'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className={`h-2 w-2 shrink-0 rounded-full ${sourceColor}`} />
        <span className="truncate font-medium">{collection.title}</span>
      </div>
      
      <div className="flex items-center gap-1 shrink-0">
        {/* Actions (visibles au hover ou si sélectionné) */}
        {canEdit && (
          <div className={`flex gap-0.5 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
            <button
              type="button"
              onClick={handleStartEdit}
              className="rounded p-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
              title="Renommer"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleDeleteClick}
              className="rounded p-1 text-zinc-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/40 dark:hover:text-red-400"
              title="Supprimer"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Compteur */}
        <span className="ml-1 rounded-full bg-zinc-200/80 px-2 py-0.5 text-xs font-semibold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">
          {contentCount}
        </span>
      </div>
    </div>
  )
}
